import { Media } from '@syconium/little-miss-figgy'

import { useLocalization } from '../../../../../../app/_providers/LocalizationProvider.client'
import { useTranslation } from '../../../../../../app/_providers/TranslationProvider.client'
import { IntlShippingAndTaxMsg } from '../../../../../../brunswick/components/IntlShippingAndTaxMsg'
import { RefsContainer } from '../../../../../../brunswick/containers/refs'
import { AddToBagContainer } from '../../contexts/AddToBagContainer'
import { ProductDetailsContainer } from '../../contexts/ProductDetailsContainer'
import { ProductHighlights } from '../../ProductHighlights'
import WhyWeLove from '../../WhyWeLove'
import { AddToBagButton } from '../AddToBagButton'
import { CollectionLinks } from '../CollectionLink'
import { ProductDetailsAccordionSection } from '../ProductDetailsAccordionSection'
import * as SharedStyles from '../styles'

import * as Styles from './styles'

type GarmentDetailsProps = {
  /** The selectable attributes that are displayed for the current product. */
  attributes: JSX.Element
}

/**
 * Renders the necessary product details for the current product.
 *
 * Responsible for displaying price, details, and informational sections for the current product.
 * Selectable attributes for the product are provided by the parent component via the `attributes` prop.
 */
export default function GarmentDetails({ attributes }: GarmentDetailsProps) {
  const {
    detailsSection: { freeShippingMessage, freeReturnsMessage },
    pages: { product: productPageStrings },
  } = useTranslation()

  const {
    featuredProduct,
    productGroup,
    selectedVariant,
    isProductOnWaitlist,
    isProductFinalSale,
    wwltImage,
    collectionLinks,
    promoText,
  } = ProductDetailsContainer.useContainer()

  const { swPromoEligible } = featuredProduct
  const { detailsSectionAddToBagSectionRef } = RefsContainer.useContainer()

  const { addToBagCta, onAddToBagClick, isAddToCartInFlight, isAddToBagDisabled } =
    AddToBagContainer.useContainer()

  const { isUSRegion } = useLocalization()

  return (
    <div>
      {/* The price & tags for the current product */}
      <ProductHighlights />

      {/* Promotional messaging for the current product */}
      {promoText && (
        <SharedStyles.PromoText data-testid='promo-text'>{promoText}</SharedStyles.PromoText>
      )}

      {/* Render the attribute selectors for the current product */}
      {attributes}

      {/* Add to bag button */}
      <SharedStyles.AddToBagButtonContainer ref={detailsSectionAddToBagSectionRef}>
        <AddToBagButton
          onClickAddToBag={onAddToBagClick}
          isProcessing={isAddToCartInFlight}
          isDisabled={isAddToBagDisabled}
          isWaitlist={isProductOnWaitlist}
          cta={addToBagCta}
        />
      </SharedStyles.AddToBagButtonContainer>

      {/* Display shipping/tax information for the current locale */}
      <Styles.IntlMsgWrap>
        <IntlShippingAndTaxMsg />
      </Styles.IntlMsgWrap>

      {/* Promo exclusion messaging */}
      {!swPromoEligible && (
        <Styles.PromoExclusionMessage>
          {productPageStrings.notEligibleForDiscount}
        </Styles.PromoExclusionMessage>
      )}

      {/* Free shipping messaging for domestic customers */}
      {isUSRegion && (
        <SharedStyles.ProductDetailsCopy style={{ margin: '24px 0', textAlign: 'center' }}>
          <Styles.ProductDetailsTitle>
            {freeShippingMessage} {!isProductFinalSale && freeReturnsMessage}
          </Styles.ProductDetailsTitle>
        </SharedStyles.ProductDetailsCopy>
      )}

      {/* WWL mobile section */}
      <Media lessThan='md'>
        <>
          <Styles.WhyWeLoveDivider />
          <WhyWeLove
            imageSrc={wwltImage?.source}
            whyWeLoveText={featuredProduct.whyWeLove}
            whyWeLoveIcons={featuredProduct.iconList}
          />
        </>
      </Media>

      {/* WWL desktop section */}
      <Media greaterThanOrEqual='md'>
        <Styles.WhyWeLoveText>{featuredProduct.whyWeLove}</Styles.WhyWeLoveText>
      </Media>

      {/* Accordions for product specifications (i.e. Details & Fit, Fabric & Care)*/}
      <ProductDetailsAccordionSection
        careInstructions={featuredProduct.careInstructions}
        fitModelsDescriptions={featuredProduct.fitModelsDescriptions}
        productGroupRtfFeatures={productGroup.rtfFeatures}
        productRtfFeatures={featuredProduct.rtfFeatures}
        rawMaterials={featuredProduct.rawMaterials}
        upc={selectedVariant?.upc}
      />

      {/* Links to collections related to the current product tags/selected variant color */}
      <CollectionLinks links={collectionLinks} />
    </div>
  )
}
